import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  CHANGE_PROCESS_TYPES,
  CHANGE_REASON_STATUSES,
  RECORD_STATUSES,
  changeReasonInputSchema,
} from '@app/shared';
import type { ChangeReasonDto, ChangeReasonInput } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { StatusChip } from '../../components/StatusChip';
import { FormDialog } from '../../components/dialogs';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { formatDate } from '../../lib/format';
import { useListState } from '../../lib/useListState';
import { useChangeReasons, useCreateChangeReason, useUpdateChangeReason } from './api';
import { withRequiredMessages } from './formUtils';

/** The status select starts empty so the user has to choose one. */
type FormValues = Omit<ChangeReasonInput, 'reasonStatus'> & {
  reasonStatus: ChangeReasonInput['reasonStatus'] | '';
};

const EMPTY: FormValues = { name: '', reasonStatus: '', processType: 'BOTH', status: 'Active' };

const resolver = withRequiredMessages<FormValues>(zodResolver(changeReasonInputSchema), {
  reasonStatus: 'Select a change reason status',
});

function ChangeReasonDialog(props: {
  open: boolean;
  reason?: ChangeReasonDto;
  onClose: () => void;
}) {
  const { open, reason, onClose } = props;
  const notify = useNotify();
  const createReason = useCreateChangeReason();
  const updateReason = useUpdateChangeReason();
  const { control, handleSubmit, reset, setError } = useForm<FormValues>({
    defaultValues: EMPTY,
    resolver,
  });

  useEffect(() => {
    if (!open) return;
    reset(
      reason
        ? {
            name: reason.name,
            reasonStatus: reason.reasonStatus,
            processType: reason.processType,
            status: reason.status,
          }
        : EMPTY,
    );
  }, [open, reason, reset]);

  const submit = handleSubmit(async (values) => {
    // The resolver only lets a chosen status through.
    const input = values as ChangeReasonInput;
    try {
      if (reason) {
        await updateReason.mutateAsync({ id: reason.id, input });
        notify.success('Change reason updated.');
      } else {
        await createReason.mutateAsync(input);
        notify.success('Change reason added.');
      }
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <FormDialog
      open={open}
      title={reason ? 'Update Change Reason' : 'Add Change Reason'}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={createReason.isPending || updateReason.isPending}
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <TextInput control={control} name="name" label="Reason" required autoFocus />
        <SelectInput
          control={control}
          name="reasonStatus"
          label="Change Reason Status"
          required
          options={toOptions(CHANGE_REASON_STATUSES)}
          helperText="Status the old SIM or device takes when this reason is used"
        />
        <SelectInput
          control={control}
          name="processType"
          label="Process Type"
          options={toOptions(CHANGE_PROCESS_TYPES)}
        />
        <SelectInput
          control={control}
          name="status"
          label="Status"
          options={toOptions(RECORD_STATUSES)}
        />
      </Box>
    </FormDialog>
  );
}

/** Manage Change Reason (PC732): the reference's "Change ESN/Device Reason List". */
export function ChangeReasonsPage() {
  const list = useListState({ q: '', reasonStatus: '', status: '', processType: '' }, 10, 'name');
  const reasons = useChangeReasons(list.params);
  const [form, setForm] = useState<{ reason?: ChangeReasonDto } | null>(null);

  const columns: Column<ChangeReasonDto>[] = [
    { key: '#', header: '#', render: (_r, i) => rowNumber(i, list.page, list.pageSize), width: 50 },
    { key: 'name', header: 'Reason', sortable: true, render: (r) => r.name },
    {
      key: 'reasonStatus',
      header: 'Change Reason Status',
      sortable: true,
      render: (r) => <StatusChip status={r.reasonStatus} />,
    },
    { key: 'processType', header: 'Process Type', sortable: true, render: (r) => r.processType },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (r) => <StatusChip status={r.status} />,
    },
    {
      key: 'createdAt',
      header: 'Created Date',
      sortable: true,
      nowrap: true,
      render: (r) => formatDate(r.createdAt),
    },
    { key: 'createdBy', header: 'Created By', render: (r) => r.createdBy?.loginId ?? '' },
    {
      key: 'updatedAt',
      header: 'Updated Date',
      sortable: true,
      nowrap: true,
      render: (r) => formatDate(r.updatedAt),
    },
    { key: 'updatedBy', header: 'Updated By', render: (r) => r.updatedBy?.loginId ?? '' },
    {
      key: 'action',
      header: 'Action',
      render: (r) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => setForm({ reason: r })}
            aria-label={`Edit reason ${r.name}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Change ESN/Device Reason List"
        code="PC732"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setForm({})}>
            Add Change Reason
          </Button>
        }
      />
      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          label="Reason"
          value={list.draft.q}
          onChange={(e) => list.setField('q', e.target.value)}
        />
        <TextField
          select
          label="Change Reason Status"
          value={list.draft.reasonStatus}
          onChange={(e) => list.setField('reasonStatus', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {CHANGE_REASON_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          value={list.draft.status}
          onChange={(e) => list.setField('status', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {RECORD_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Process Type"
          value={list.draft.processType}
          onChange={(e) => list.setField('processType', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {CHANGE_PROCESS_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
      </FilterPanel>

      <DataTable
        label="Change ESN/Device Reason List"
        columns={columns}
        rows={reasons.data?.data}
        rowKey={(r) => r.id}
        loading={reasons.isFetching}
        error={reasons.error}
        onRetry={() => void reasons.refetch()}
        page={list.page}
        pageSize={list.pageSize}
        total={reasons.data?.total ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        pageSizeOptions={[10, 20, 50, 100]}
        sort={list.sort}
        onSortChange={list.setSort}
      />

      <ChangeReasonDialog
        open={Boolean(form)}
        reason={form?.reason}
        onClose={() => setForm(null)}
      />
    </Box>
  );
}
